//@ts-ignore
import React, { useState } from 'react';
import { Modal, Button, Radio, Select, message } from 'antd';
import { DeleteFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
const { Option } = Select;

interface DeletePopupProps {
    onDelete: (deleteAll: boolean, fromYear: number, toYear: number) => void;
    indicatorId: string | undefined;
    indicatorIds: number[];
}

const DeletePopup: React.FC<DeletePopupProps> = ({ onDelete, indicatorId, indicatorIds }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [deleteOption, setDeleteOption] = useState<'all' | 'range'>('all');
    const [fromYear, setFromYear] = useState<number | undefined>();
    const [toYear, setToYear] = useState<number | undefined>();
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => 2000 + i); // Years from 2000 to current year

    const showDeletePopup = () => {
        setIsVisible(true);
    };

    const handleDeleteOptionChange = (e: any) => {
        setDeleteOption(e.target.value);
    };

    const handleDelete = () => {
        if (deleteOption === 'range' && (fromYear === undefined || toYear === undefined || toYear < fromYear)) {
            message.error(t('msg.invalid.year.range'));
            return;
        }
        setIsConfirmVisible(true);
    };

    const handleConfirmDelete = () => {
        if (deleteOption === 'range') {
            // Ensure both years are selected and valid
            if (fromYear === undefined || toYear === undefined || toYear < fromYear) {
                message.error(t('msg.invalid.year.range'));
                return;
            }
        }

        setIsConfirmVisible(false);
        setIsVisible(false);
        // Pass fallback values if necessary
        onDelete(deleteOption === 'all', fromYear ?? currentYear, toYear ?? currentYear);

        // Reset state
        setFromYear(undefined);
        setToYear(undefined);
        setDeleteOption('all');
    };

    const handleCancel = () => {
        setIsVisible(false);
        setFromYear(undefined);
        setToYear(undefined);
        setDeleteOption('all');
    };

    return (
        <>
            <Button
                disabled={indicatorId && indicatorIds.includes(parseInt(indicatorId)) ? false : true}
                onClick={showDeletePopup}
                type="primary"
            >
                <DeleteFilled /> {t('delete.data')}
            </Button>

            <Modal
                title={t('msg.delete.data')}
                open={isVisible}
                width={600}
                onCancel={() => setIsVisible(false)}
                className="highlighted-modal"
                footer={[
                    <Button key="cancel" onClick={handleCancel}>
                        {t('btn.cancel')}
                    </Button>,
                    <Button key="delete" type="primary" onClick={handleDelete}>
                        {t('delete.title')}
                    </Button>,
                ]}
            >
                <div className='deleteModel'>
                    <Radio.Group onChange={handleDeleteOptionChange} value={deleteOption}>
                        <Radio value="all">{t('delete.all.data')} </Radio>
                        <Radio value="range">{t('delete.year.range')} </Radio>
                    </Radio.Group>

                    {deleteOption === 'range' && (
                        <div style={{ marginTop: 16 }}>
                            <Select
                                placeholder={t('delete.from.year')}
                                style={{ width: '45%', marginRight: '10%' }}
                                onChange={(value) => setFromYear(value)}
                                value={fromYear}
                                showSearch={true}
                                allowClear
                            >
                                {years.map((year) => (
                                    <Option key={year} value={year}>
                                        {year}
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                placeholder={t('delete.to.year')}
                                style={{ width: '45%' }}
                                onChange={(value) => setToYear(value)}
                                disabled={fromYear === undefined}
                                value={toYear}
                            >
                                {years
                                    .filter((year) => fromYear === undefined || year >= fromYear)
                                    .map((year) => (
                                        <Option key={year} value={year}>
                                            {year}
                                        </Option>
                                    ))}
                            </Select>
                        </div>
                    )}
                </div>
            </Modal>
            <Modal
                title={t('msg.Confirm.delete')}
                open={isConfirmVisible}
                onCancel={() => setIsConfirmVisible(false)}
                onOk={handleConfirmDelete}
                okText={t('yes')}
                cancelText={t('no')}
            >
                <div className='deleteModel'>
                    {deleteOption === 'all'
                        ? t('msg.delete.all.data')
                        : `${t('msg.delete.all.data.range')} ${fromYear}-${toYear}?`}
                </div>
            </Modal>
        </>
    );
};

export default DeletePopup;
